import type { ErrorEvent, EventHint } from '@sentry/core';

import {
  eventHasError,
  exceptionsFromEvent,
  frameFilenameIncludes,
  framesFromException,
  isSentryTestException,
  withoutFirstSentryFrame,
} from './utils';

const filterHintError = (hint: EventHint, message: string): boolean => {
  const error = hint.originalException;
  let errorMessage = '';
  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error instanceof Error) {
    errorMessage = error.message;
  }

  return errorMessage.includes(message);
};

const isGoogleTagErrorEvent = (event: ErrorEvent): boolean =>
  eventHasError(event, {
    type: 'TypeError',
    value: 'Illegal invocation',
    path: 'gtag',
  }) ||
  eventHasError(event, {
    type: 'fetch_generic_error',
    path: 'gtag',
  }) ||
  eventHasError(event, {
    type: 'TypeError',
    value: 'localStorage',
    path: '/ping.min.js',
  }) ||
  // ALM-3779: GTM related event
  eventHasError(event, {
    path: '/scripts/eid.es5.js',
  }) ||
  // ALM-3780: Do not react to "grammarly SDK" related errors
  eventHasError(event, {
    value: 'Cannot convert undefined or null to object',
    path: 'grammarly-editor-sdk',
  });

const isThirdPartyLibraryErrorEvent = (_event: ErrorEvent, hint: EventHint): boolean => {
  if (!hint) {
    return false;
  }
  return (
    filterHintError(hint, 'privateSpecialRepair is not defined') ||
    filterHintError(hint, '$ is not defined') ||
    filterHintError(hint, 'chrome is not defined') ||
    filterHintError(hint, 'GTS is not defined') ||
    filterHintError(hint, "Unexpected token '<'") ||
    filterHintError(hint, "Unexpected token 'var'") ||
    // see https://stackoverflow.com/questions/72488297/referenceerror-getvaluesofautofillinputs-cant-find-variable-paymentautofillco
    filterHintError(hint, "Can't find variable: PaymentAutofillConfig")
  );
};

// Ignore errors if stacktrace starts with <anonymous> module or from other irrelevant modules
const fromIrrelevantModulesEvent = (event: ErrorEvent): boolean => {
  const exceptions = exceptionsFromEvent(event);

  // Always allow "test" exception
  if (isSentryTestException(exceptions[0])) {
    return false;
  }

  const frames = framesFromException(exceptions[0]);
  const cleanedFrames = withoutFirstSentryFrame(frames);
  const firstFrame = cleanedFrames?.[0];

  return (
    firstFrame?.filename === '<anonymous>' ||
    frameFilenameIncludes(firstFrame, 'chrome-extension://') ||
    frameFilenameIncludes(firstFrame, 'clarity.ms') ||
    frameFilenameIncludes(firstFrame, 'taboola.com') ||
    frameFilenameIncludes(firstFrame, 'clearbit.com')
  );
};

export const shouldSentryEventBeSend = (event: ErrorEvent, hint: EventHint): boolean => {
  if (!event) {
    return false;
  }

  if (fromIrrelevantModulesEvent(event)) {
    return false;
  }

  if (isGoogleTagErrorEvent(event)) {
    return false;
  }

  if (isThirdPartyLibraryErrorEvent(event, hint)) {
    return false;
  }

  // By default, all events sent to Sentry
  return true;
};

export const prepareIgnoreErrors = (): string[] => {
  // Inherit Sentry community's compiled a list of common ignore rules from https://docs.sentry.io/platforms/node/configuration/filtering
  return [
    // Random plugins/extensions
    'top.GLOBALS',
    // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
    'originalCreateNotification',
    'canvas.contentDocument',
    'MyApp_RemoveAllHighlights',
    'http://tt.epicplay.com',
    "Can't find variable: ZiteReader",
    'jigsaw is not defined',
    'ComboSearch is not defined',
    'http://loading.retry.widdit.com/',
    'atomicFindClose',
    // Facebook borked
    'fb_xd_fragment',
    // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
    // reduce this. (thanks @acdha)
    // See http://stackoverflow.com/questions/4113268
    'bmi_SafeAddOnload',
    'EBCallBackMessageReceived',
    // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
    'conduitPage',
    // See https://github.com/getsentry/sentry-javascript/issues/2546#issuecomment-1727939255
    'Non-Error exception captured',
    'Non-Error promise rejection captured',

    // From time to time we get this CORS-related errors thrown from one of our 3rd party integrations: gtm
    // We don't care about these to push them to Sentry.
    'Blocked a frame with origin',
  ];
};

export const prepareDenyUrls = (): RegExp[] => {
  // Sentry community's compiled a list of common ignore rules
  //   https://docs.sentry.io/platforms/node/configuration/filtering/
  return [
    // Facebook flakiness
    /graph\.facebook\.com/i,
    // Facebook blocked
    /connect\.facebook\.net\/en_US\/all\.js/i,
    // Woopra flakiness
    /eatdifferent\.com\.woopra-ns\.com/i,
    /static\.woopra\.com\/js\/woopra\.js/i,
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
    // Other plugins
    /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
    /webappstoolbarba\.texthelp\.com\//i,
    /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
    /cdn\.nmgassets\.com/i, // namogoo scripts
    /assets\.apollo\.io\/micro\/website-tracker\//i, // Apollo website tracker
    /static\.zohocdn\.com\/zoho-salesiq-widgets\//i, // Zoho SalesIQ chat widget
    /consent\.cookiebot\.com\//i, // Cookiebot consent
  ];
};
